/**
 * resilientOnSnapshot — a re-assinatura que NENHUM listener do app tinha.
 *
 * ## O buraco (mapa de 29/08, §BUG-179 adjacentes)
 *
 * O SDK do Firestore só chama o callback de ERRO de um `onSnapshot` quando o
 * erro é TERMINAL para aquele listener (permission-denied, unauthenticated,
 * failed-precondition, internal…) — falha transitória de rede ele re-tenta
 * sozinho e nunca reporta. Quando o callback de erro dispara, **o listener
 * está morto e não volta**. Medido em 29/08: os 16 `onSnapshot` de `src/`
 * tratavam esse callback como log-e-segue — nenhum re-assinava. Consequência:
 * um erro real num dos três listeners globais (perfil, stats, estante) deixava
 * a conta degradada até matar o app.
 *
 * E parte desses erros terminais é RECUPERÁVEL por re-assinatura: o clássico é
 * `unauthenticated` na volta do background — o token expirou durante a
 * suspensão, o listener morre na corrida com o refresh do Auth, e um attach
 * novo 1s depois já encontra o token renovado.
 *
 * ## O desenho
 *
 *  · erro → re-assina com backoff (1s · 2s · 4s · 8s), SEM avisar o consumidor
 *    ainda — avisar no primeiro erro faria a UI piscar o estado vazio durante
 *    uma recuperação que costuma fechar em segundos;
 *  · snapshot entregue → contador de tentativas zera (um listener vivo por
 *    horas ganha fôlego novo a cada incidente);
 *  · tentativas esgotadas → `onError` do consumidor UMA vez, e o listener fica
 *    morto como sempre ficou — a degradação de cada tela continua a dela.
 *
 * O teto existe porque erro PERMANENTE de verdade (regra negando um doc que
 * nunca será legível) não pode virar retry infinito: 4 tentativas custam no
 * máximo 4 reads e ~15s, e param.
 */
import type { Unsubscribe } from "firebase/firestore";
import { traceResume } from "./resumeTrace";

export const RESILIENT_SNAPSHOT_MAX_RETRIES = 4;
export const RESILIENT_SNAPSHOT_BASE_DELAY_MS = 1_000;

/**
 * ── Ressurreição na retomada (continuação do BUG-197, 01/09) ────────────────
 *
 * O QA do build com o diário provou episódios em que TODAS as sondas passam
 * (`wake → paint-ok → verify-ok`) e a tela segue sem carregar. Um mecanismo
 * fecha exatamente esse quadro: listener que ESGOTOU as 4 retentativas fica
 * morto até matar o app — e o roteiro real do fundador (5 retomadas em 4 min,
 * cada uma com corrida de refresh de token) é o que esgota o teto. A sonda de
 * entrega não o vê: ela mede o TRANSPORTE, não quem assina.
 *
 * O registro abaixo guarda a re-assinatura de cada listener morto; o
 * `FirestoreResumeWatcher` chama `reviveDeadFirestoreListeners()` quando a
 * retomada PROVA transporte são (verify-ok no nativo, fila viva na web) — que
 * é o único momento em que gastar um orçamento novo de retentativas faz
 * sentido. A morte agora também deixa rastro (`listener-dead`, anormal): é o
 * discriminador que faltava no diário para este modo de falha.
 */
const deadListeners = new Set<() => void>();

/**
 * Re-assina todos os listeners que morreram por esgotamento, com orçamento
 * cheio de retentativas. Devolve quantos foram ressuscitados (0 = não havia
 * mortos — o caso comum, custo zero).
 */
export function reviveDeadFirestoreListeners(): number {
  const toRevive = [...deadListeners];
  deadListeners.clear();
  for (const revive of toRevive) revive();
  return toRevive.length;
}

/**
 * `attach` cria o listener de verdade (um `onSnapshot` com os callbacks
 * injetados) e devolve o unsubscribe dele. Injetado para o teste não precisar
 * de um Firestore real — mesmo padrão da sonda de `firestoreHealth.ts`.
 */
export function resilientOnSnapshot<T>(
  attach: (onNext: (value: T) => void, onError: (err: Error) => void) => Unsubscribe,
  onNext: (value: T) => void,
  onError?: (err: Error) => void,
  opts?: { maxRetries?: number; baseDelayMs?: number },
): Unsubscribe {
  const maxRetries = opts?.maxRetries ?? RESILIENT_SNAPSHOT_MAX_RETRIES;
  const baseDelayMs = opts?.baseDelayMs ?? RESILIENT_SNAPSHOT_BASE_DELAY_MS;

  let disposed = false;
  let retries = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let currentUnsub: Unsubscribe | null = null;
  // As re-assinaturas que ESTE listener registrou no registro global — o
  // dispose as remove, senão um listener desmontado (troca de conta, unmount)
  // seria ressuscitado órfão na próxima retomada.
  const revives = new Set<() => void>();

  const start = () => {
    if (disposed) return;
    currentUnsub = attach(
      (value) => {
        if (disposed) return;
        retries = 0;
        onNext(value);
      },
      (err) => {
        if (disposed) return;
        // O listener que errou está morto; o unsubscribe dele vira no-op, mas
        // guardar a referência limpa evita chamar unsubscribe de um attach
        // antigo no dispose.
        currentUnsub = null;
        if (retries >= maxRetries) {
          console.warn(
            "[Lura] listener do Firestore morreu depois de re-tentativas.",
            err.message,
          );
          traceResume("listener-dead", err.message);
          // A degradação da tela continua a dela (onError abaixo) — mas o
          // listener deixa de estar morto PARA SEMPRE: a próxima retomada com
          // transporte provado o re-assina com orçamento cheio. Registrar em
          // vez de re-tentar aqui é deliberado: sem um sinal externo de saúde,
          // retry infinito num erro permanente (regra negando) seria custo puro.
          const revive = () => {
            if (disposed) return;
            retries = 0;
            start();
          };
          deadListeners.add(revive);
          revives.add(revive);
          onError?.(err);
          return;
        }
        const delay = baseDelayMs * 2 ** retries;
        retries += 1;
        timer = setTimeout(() => {
          timer = undefined;
          start();
        }, delay);
      },
    );
  };

  start();

  return () => {
    disposed = true;
    if (timer !== undefined) clearTimeout(timer);
    for (const revive of revives) deadListeners.delete(revive);
    revives.clear();
    currentUnsub?.();
    currentUnsub = null;
  };
}
